import { useEffect } from "react";
import { useHistory } from "react-router-dom";

import type { AssessmentFormValues, TaskType } from "@/core/types";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { tryCatch } from "@/lib/helpers/tryCatch";
import { useTaskIdByType } from "@/lib/hooks/useTaskIdByType";
import { showToast } from "@/providers/ToastProvider";

import { useSubmitAssessment } from "../api/mutations/useSubmitAssessment";
import { useAssessmentDefinition } from "../api/queries/useAssessmentDefinition";

import { AssessmentError } from "./AssessmentError";
import { AssessmentRenderer } from "./AssessmentRenderer";
import { AssessmentSkeleton } from "./AssessmentSkeleton";

type AssessmentContainerProps = {
  definitionId: string;
  taskType: TaskType;
};

export function AssessmentContainer({ definitionId, taskType }: AssessmentContainerProps) {
  const history = useHistory();
  const taskId = useTaskIdByType(taskType);
  const {
    data: assessment,
    error,
    isLoading,
  } = useAssessmentDefinition({
    id: definitionId,
  });

  const { isLoading: isMutating, mutateAsync: submitAssessmentAnswers } = useSubmitAssessment({
    taskId,
  });

  useEffect(() => {
    // User should not submit assessment without valid taskId
    if (!taskId) history.push("/app/home");
  }, [taskId, history]);

  if (isLoading) return <AssessmentSkeleton />;

  if (error || !assessment) {
    return <AssessmentError definitionId={definitionId} error={error} />;
  }

  const handleSubmit = async (formData: AssessmentFormValues) => {
    const [, error] = await tryCatch(
      submitAssessmentAnswers({
        assessmentDefinitionId: assessment.id,
        assessmentDefinitionVersion: assessment.version,
        formData,
        taskId,
      }),
    );

    if (error) {
      captureErrorMessage(parseError({ error, fallback: "Assessment submission error" }), {
        tags: {
          assessmentId: assessment.id,
          assessmentName: assessment.name,
          component: "AssessmentContainer",
          errorType: "assessment_submission",
        },
      });

      showToast([
        {
          heading: "Something went wrong",
          text: "Please try submitting again.",
          variant: "error",
        },
      ]);
    }
  };

  return (
    <AssessmentRenderer assessment={assessment} isSubmitting={isMutating} onSubmit={handleSubmit} />
  );
}
